/* src/modules/peer/AppreciationDashboard.js */
import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
    TrendingUpIcon, TrophyIcon 
} from '../../components/icons/Icons';

// --- CONFIGURATION ---
const BRAND_COLORS = {
    darkBlue: '#213368',
    teal: '#20BEC6',
    lightTeal: '#C9E9E6',
    lightGrey: '#EFEFEF',
    orange: '#F4A866',
    red: '#D95448'
};

const ICONS = {
    commitment: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>,
    collaboration: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    customer: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
};

const VALUES = {
    commitment: { label: 'Commitment', color: BRAND_COLORS.orange, bg: '#FFF3E0', icon: ICONS.commitment },
    collaboration: { label: 'Collaboration', color: BRAND_COLORS.teal, bg: BRAND_COLORS.lightTeal, icon: ICONS.collaboration },
    customer: { label: 'Customer', color: BRAND_COLORS.red, bg: '#FEE2E2', icon: ICONS.customer }
};

// --- HELPERS ---
const UserAvatar = ({ name, photoUrl, size = 'md' }) => {
    const sizeClasses = size === 'lg' ? 'h-16 w-16 text-2xl' : 'h-10 w-10 text-sm';
    if (photoUrl) return <img src={photoUrl} alt={name} className={`${sizeClasses} rounded-full object-cover border border-gray-200 shadow-sm`} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />;
    return <div className={`${sizeClasses} rounded-full flex items-center justify-center font-bold shadow-sm`} style={{ backgroundColor: BRAND_COLORS.lightTeal, color: BRAND_COLORS.darkBlue }}>{name?.charAt(0).toUpperCase() || 'U'}</div>;
};

const calculateLevel = (points) => {
    const safePoints = points || 0;
    const level = Math.floor(safePoints / 100) + 1;
    const nextLevelPoints = level * 100;
    const progress = ((safePoints % 100) / 100) * 100;
    return { level, nextLevelPoints, progress };
};

const formatDate = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }).format(date);
    } catch (e) { return '-'; }
};

export default function AppreciationDashboard({ userProfile, appId }) {
    const [liveUser, setLiveUser] = useState(userProfile);
    const [receivedList, setReceivedList] = useState([]);
    const [sentList, setSentList] = useState([]);
    const [viewMode, setViewMode] = useState('received'); 
    const [isLoading, setIsLoading] = useState(true);
    const [wins, setWins] = useState([]); // Trophy Case State
    
    // 1. STABLE ID
    const MY_ID = userProfile.uid || userProfile.id; 

    useEffect(() => {
        // A. Listen to Real-time User Stats
        const userRef = doc(db, `artifacts/${appId}/users`, MY_ID);
        const unsubUser = onSnapshot(userRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveUser(prev => ({ ...prev, ...docSnap.data() })); 
            }
        });

        // B. Fetch Appreciations & Filter client-side (Robust filtering)
        const appRef = collection(db, `artifacts/${appId}/appreciations`);
        const qApp = query(appRef, orderBy('createdAt', 'desc'));

        const unsubApp = onSnapshot(qApp, (snapshot) => {
            const allDocs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            const myReceived = allDocs.filter(item => {
                const docToId = item.toUserId || item.toUserID || item.touserid;
                return docToId === MY_ID;
            });
            
            const mySent = allDocs.filter(item => {
                const docFromId = item.fromUserId || item.fromUserID || item.fromuserid;
                return docFromId === MY_ID;
            });

            setReceivedList(myReceived);
            setSentList(mySent);
            setIsLoading(false);
        });

        // C. Listen for Wins (Trophy Case)
        const challRef = collection(db, `artifacts/${appId}/challenges`);
        const qWins = query(challRef, where('winners', 'array-contains', MY_ID));
        
        const unsubWins = onSnapshot(qWins, (snap) => {
            setWins(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubUser(); unsubApp(); unsubWins(); };
    }, [appId, MY_ID]);

    if (isLoading) return <LoadingSpinner />;

    // Helper for badges
    const rawBadges = liveUser.badges || liveUser.Badges || {};
    const badges = {
        commitment: rawBadges.commitment || rawBadges.Commitment || 0,
        collaboration: rawBadges.collaboration || rawBadges.Collaboration || 0,
        customer: rawBadges.customer || rawBadges.Customer || 0
    };

    const currentPoints = liveUser.points || 0;
    const { level, nextLevelPoints, progress } = calculateLevel(currentPoints);

    return (
        <div className="space-y-8 animate-fadeIn">
            
            {/* --- NEW: TROPHY CASE (Only show if they have wins) --- */}
            {wins.length > 0 && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
                    <h3 className="text-xl font-bold text-yellow-800 mb-4 flex items-center">
                        <TrophyIcon className="h-6 w-6 mr-2 text-yellow-600" />
                        My Trophy Case
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {wins.map(win => (
                            <div key={win.id} className="bg-white p-4 rounded-lg shadow-sm border border-yellow-100 flex items-center">
                                <div className="p-3 bg-yellow-100 rounded-full mr-3 text-yellow-600">
                                    <TrophyIcon className="h-6 w-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-sm">{win.title}</h4>
                                    <p className="text-xs text-gray-500">
                                        Won on {win.endDate ? new Date(win.endDate.toDate()).toLocaleDateString() : '-'}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* --- STATS CARD --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between relative overflow-hidden">
                    <div className="flex items-center space-x-4 z-10">
                        <UserAvatar name={liveUser.name} photoUrl={liveUser.photoURL} size="lg" />
                        <div>
                            <h3 className="text-gray-500 font-medium uppercase text-xs tracking-wider">Total Points</h3>
                            <p className="text-4xl font-extrabold" style={{ color: BRAND_COLORS.teal }}>{currentPoints}</p>
                        </div>
                    </div>
                    <div className="mt-6 z-10">
                        <div className="flex justify-between text-sm mb-1">
                            <span className="font-bold text-[#213368]">Level {level}</span>
                            <span className="text-gray-400">{currentPoints} / {nextLevelPoints}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                            <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: BRAND_COLORS.darkBlue }}></div>
                        </div>
                    </div>
                    <div className="absolute top-[-10px] right-[-10px] opacity-5 transform rotate-12">
                        <TrendingUpIcon className="h-32 w-32 text-[#213368]" />
                    </div>
                </div>

                {/* Badges */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {Object.entries(VALUES).map(([key, val]) => (
                        <div key={key} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center justify-center hover:shadow-md transition">
                            <div className="p-3 rounded-full mb-3" style={{ backgroundColor: val.bg, color: val.color }}>
                                {val.icon}
                            </div>
                            <h4 className="font-bold text-gray-700">{val.label}</h4>
                            <p className="text-2xl font-bold mt-1" style={{ color: val.color }}>{badges[key]}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* --- HISTORY LIST --- */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex border-b border-gray-200">
                    <button 
                        onClick={() => setViewMode('received')}
                        className={`flex-1 py-4 text-center font-medium text-sm transition ${viewMode === 'received' ? 'bg-blue-50 text-[#213368] border-b-2 border-[#213368]' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Received ({receivedList.length})
                    </button>
                    <button 
                        onClick={() => setViewMode('sent')}
                        className={`flex-1 py-4 text-center font-medium text-sm transition ${viewMode === 'sent' ? 'bg-blue-50 text-[#213368] border-b-2 border-[#213368]' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Sent ({sentList.length})
                    </button>
                </div>

                <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                    {(viewMode === 'received' ? receivedList : sentList).length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            No records found.
                        </div>
                    )}

                    {(viewMode === 'received' ? receivedList : sentList).map(item => {
                        const safeCategory = (item.valueCategory || 'commitment').toLowerCase();
                        const valueConfig = VALUES[safeCategory] || VALUES['commitment'];
                        
                        const fromName = item.fromUserName || item.FromUserName || item.fromusername || 'Unknown';
                        const toName = item.toUserName || item.ToUserName || item.tousername || 'Unknown';
                        const targetName = viewMode === 'received' ? fromName : toName;
                        
                        return (
                            <div key={item.id} className="p-4 hover:bg-gray-50 transition flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <UserAvatar name={targetName} />
                                </div>
                                <div className="flex-grow">
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm font-medium text-[#213368]">
                                            {viewMode === 'received' 
                                                ? <><span className="text-gray-500">From:</span> {fromName}</>
                                                : <><span className="text-gray-500">To:</span> {toName}</>
                                            }
                                        </p>
                                        <span className="text-xs text-gray-400 whitespace-nowrap">
                                            {formatDate(item.createdAt)}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-bold" style={{ color: valueConfig.color }}>
                                        {valueConfig.label} • {item.subValue}
                                    </p>
                                    <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-2 rounded border border-gray-100 italic">"{item.message}"</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div> 
    );
}